import {
    ComponentRef,
    inject,
    Injectable,
    NgModuleRef,
    TemplateRef,
    Type,
} from '@angular/core';
import { AppConstantsConfig, SafeAny } from '@pf/shared-common';
import { DrawerComponent } from '../../../interfaces/DrawerComponent';
import { DrawerFormComponent } from '../../forms/components/drawer-form.component';
import { InjectionService } from '../../../services/injection.service';
import { FormService } from '../../forms/services/form.service';
import {
    APP_CONSTANTS_CONFIG,
    ResolveTemplate,
} from '../../../pipes/app-constants.pipe';
import { NzDrawerRef, NzDrawerService } from 'ng-zorro-antd/drawer';

export enum DrawerType {
    Form = 'form',
}

export const DrawerTypeComponent: Record<DrawerType, Type<DrawerComponent>> = {
    [DrawerType.Form]: DrawerFormComponent,
};

export interface CreateFormDrawerOptions<T> {
    entityType: string;
    parentFormId?: string;
    title: string;
    component: Type<T>;
    componentProps?: Partial<T>;
    hash?: string;
    navigateBackOnClose?: boolean;
    level?: number;
}

export type TemplateDrawerComponent<T> = {
    drawerRef: NzDrawerRef;
    data: T;
};

export type TemplateDrawerContent =
    | TemplateRef<{
          $implicit: SafeAny;
          drawerRef: NzDrawerRef;
      }>
    | Type<{ data: SafeAny }>
    | undefined;

export interface CreateTemplateDrawerOptions {
    title: string;
    footer?: string | TemplateRef<SafeAny>;
    extra?: string | TemplateRef<SafeAny>;
    content: TemplateDrawerContent;
    data?: Record<string, SafeAny>;
    size?: 'default' | 'large';
    isMobile?: boolean;
    placement?: 'bottom' | 'top' | 'right';
    height?: string;
    onOpen?: () => void;
    onClose?: (data?: SafeAny) => void;
}

@Injectable({
    providedIn: 'root',
})
export class DrawerService {
    private injectionService = inject(InjectionService);
    private moduleRef = inject<NgModuleRef<SafeAny>>(NgModuleRef);
    private formService = inject(FormService);
    private nzDrawerService = inject(NzDrawerService);
    private readonly appConstants = inject<AppConstantsConfig>(
        APP_CONSTANTS_CONFIG,
        { optional: true }
    );

    private readonly _drawers: Record<string, ComponentRef<DrawerComponent>> =
        {};

    createFormDrawer<T>(createDrawerOptions: CreateFormDrawerOptions<T>) {
        const level =
            createDrawerOptions.level ||
            this.injectionService.getElementsFromComponentHost(
                'root',
                'pf-drawer-form'
            ).length;

        const formState = this.formService.registerForm(
            createDrawerOptions.entityType,
            undefined,
            createDrawerOptions.parentFormId
        );

        const componentRef = this.addComponentToApp(DrawerType.Form);
        const instance = componentRef.instance;
        instance.createDrawer({
            title: ResolveTemplate(
                createDrawerOptions.title,
                this.appConstants
            ),
            entityType: createDrawerOptions.entityType,
            component: createDrawerOptions.component,
            componentProps: createDrawerOptions.componentProps,
            level,
            hash: createDrawerOptions.hash,
            navigateBackOnClose:
                createDrawerOptions.navigateBackOnClose === undefined
                    ? true
                    : createDrawerOptions.navigateBackOnClose,
        });

        // Use formState.id since it is already unique in this scenario
        this._drawers[formState.id] = componentRef;
        this.formService.setDrawerRendered(
            createDrawerOptions.entityType,
            true,
            formState.id
        );
        instance.open();
        return componentRef;
    }

    createTemplateDrawer(options: CreateTemplateDrawerOptions) {
        const placement = options.placement || 'bottom';
        const isVertical = placement === 'bottom' || placement === 'top';
        const height =
            options.height ??
            (options.isMobile && isVertical ? '85dvh' : undefined);
        const width =
            placement === 'right'
                ? options.isMobile
                    ? '90dvw'
                    : '40dvw'
                : undefined;

        const drawerRef = this.nzDrawerService.create({
            nzTitle: options.title,
            nzFooter: options.footer,
            nzExtra: options.extra,
            nzContent: options.content,
            nzData: options.data,
            nzSize: options.isMobile ? undefined : options.size ?? 'default',
            nzHeight: height,
            nzWidth: width,
            nzPlacement: placement,
            nzClosable: true,
            nzWrapClassName: options.isMobile ? 'mobile-drawer' : undefined,
        });

        drawerRef.afterOpen.subscribe(() => {
            options?.onOpen?.();
        });

        drawerRef.afterClose.subscribe(data => {
            options?.onClose?.(data);
        });

        return drawerRef;
    }

    private addComponentToApp(
        drawerType: DrawerType
    ): ComponentRef<DrawerComponent> {
        const component = DrawerTypeComponent[drawerType];
        return this.injectionService.appendComponentHost({
            component,
            hostLocation: 'root',
            ngModuleRef: this.moduleRef,
        });
    }

    drawer(id: string): DrawerComponent {
        return this._drawers[id]?.instance;
    }
}
